// Import React tools we need: useEffect (run code on load/unload),
// useRef (remember a value without re-rendering), useState (store data
// that updates the screen), useCallback (remember a function)
import { useEffect, useRef, useState, useCallback } from 'react';

// Import AWS's official WebRTC helper — this handles talking to AWS
import { SignalingClient, Role } from 'amazon-kinesis-video-streams-webrtc';

// Import AWS tools to find our "channel" (the Pi's video address on AWS)
import { KinesisVideoClient, DescribeSignalingChannelCommand, GetSignalingChannelEndpointCommand } from '@aws-sdk/client-kinesis-video';

// Import AWS tool to get STUN/TURN servers (helps two devices connect)
import { KinesisVideoSignalingClient, GetIceServerConfigCommand } from '@aws-sdk/client-kinesis-video-signaling';

// Reminder: before running this, install these packages with npm install
// npm install amazon-kinesis-video-streams-webrtc @aws-sdk/client-kinesis-video @aws-sdk/client-kinesis-video-signaling

// --- Session-persisted caching --------------------------------------------
// The channel ARN/endpoints and the ICE server list are cached in memory
// (see channelCacheRef/iceServersCacheRef further down) so a reconnect
// doesn't re-ask AWS for things that haven't changed. Mirroring that cache
// into sessionStorage means it also survives a page refresh — sessionStorage
// persists for the life of the browser tab and clears automatically when the
// tab actually closes, which is exactly the lifetime we want here (longer
// than "just this component instance", but not forever).
const SESSION_STORAGE_CHANNEL_KEY = 'piviewer:channelCache';
const SESSION_STORAGE_ICE_KEY = 'piviewer:iceServersCache';

// All sessionStorage access is wrapped in try/catch: some browsers throw
// (rather than silently no-op) when storage is disabled or full — e.g.
// private/incognito mode in some cases — and this cache is purely an
// optimization, so any failure here should just mean "skip the cache",
// never break the actual connection.
function loadFromSessionStorage(key) {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        console.log(`[PiViewer] sessionStorage read failed for ${key} — continuing without cache`, e);
        return null;
    }
}

function saveToSessionStorage(key, value) {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        console.log(`[PiViewer] sessionStorage write failed for ${key} — cache just won't survive a refresh`, e);
    }
}

function clearSessionStorage(key) {
    try {
        sessionStorage.removeItem(key);
    } catch (e) {
        // Nothing meaningful to do if even this fails — not worth logging noise for.
    }
}
// ---------------------------------------------------------------------------

// --- Configuration (hardcoded for testing) -----------------------------
// Warning: these are just for testing on your own computer, not for
// putting online for everyone to see (explained more below)
// A permanent key embedded in browser code is visible to anyone who opens
// DevTools, and it never expires on its own like a temporary STS token
// would — so if this ships, rotate the key immediately in the IAM console.

// The name of the AWS "channel" your Raspberry Pi is broadcasting to
const CHANNEL_NAME = 'BhuSignaling';

// Which AWS data center location you are using//const REGION = 'us-west-2';
const REGION = 'ap-south-1';


// Your AWS login details (like a username and password for AWS)
const CREDENTIALS = {
    accessKeyId: 'AKIA2XJFAMI3NQKWRTR3',// old key  'AKIA2XJFAMI3LDGF7ZSE', // like a username
    secretAccessKey: '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'// old key '31yZRFh9thYZLXSCJp35rDrspdziNSNZlgaYtB7Q', // like a password
};

// A random unique name for this viewer, so AWS can tell viewers apart.
// IMPORTANT: this is called fresh for every connection attempt (see
// startViewing below) rather than computed once — reusing the same
// clientId across every reconnect risked colliding with AWS's own
// teardown of the previous session using that same id (AWS documents a
// ~1 minute grace period after telling a client to disconnect before
// that connection is actually torn down server-side).
function makeClientId() {
    return 'react-viewer-' + Math.random().toString(36).slice(2, 10);
}

// A list of possible states this component can be in, written as words
// instead of typing the same text many times (avoids typos)
const STATUS = {
    IDLE: 'idle',           // nothing happening yet, waiting for button click
    CONNECTING: 'connecting', // trying to connect right now
    LIVE: 'live',            // connected, video is playing
    RECONNECTING: 'reconnecting', // connection dropped (e.g. mobile NAT timeout), rebuilding automatically
    CLOSED: 'closed',        // connection was closed/lost (user pressed Stop)
};

// Mobile carrier NAT (CGNAT) commonly drops "idle" UDP mappings around the
// ~30s mark, which kills the WebRTC path even though both sides are fine.
// There's no way to resuscitate the existing connection once that happens,
// so instead we detect it fast and transparently rebuild a fresh session.
// These control how eagerly we retry after a drop.
const RECONNECT_BASE_DELAY_MS = 1000;   // first retry waits ~1s
const RECONNECT_MAX_DELAY_MS = 10000;   // never wait longer than 10s between tries

// We never permanently give up retrying on our own — an unattended robot
// camera may have no one watching to click "Watch Stream" again, and the
// Pi's own reconnect loop can legitimately take well over a minute. This
// constant only controls when we switch from logging every attempt to
// logging quietly (see QUIET_RETRY_LOG_EVERY) so a long outage doesn't
// flood the event log.
const RECONNECT_MAX_ATTEMPTS = 8;

// Once past RECONNECT_MAX_ATTEMPTS, only log a "still retrying" heartbeat
// every Nth attempt. At the 10s capped delay, 6 attempts ≈ once a minute.
const QUIET_RETRY_LOG_EVERY = 6;

// The channel ARN and its WSS/HTTPS endpoints don't change for a given
// channel name — there's no need to ask AWS for them again on every single
// reconnect. Only the TURN credentials from GetIceServerConfig actually
// expire (each server has its own Ttl in seconds). Caching both is the
// single biggest reconnect-speed win: on a real reconnect, these two calls
// were the slowest part by far (they're what timed out in the 8s/23s
// budgets). We just refetch ICE servers a bit before they'd actually
// expire, using this safety margin.
const ICE_SERVERS_REFRESH_MARGIN_MS = 30000;
const ICE_SERVERS_DEFAULT_TTL_S = 300; // sane fallback if AWS doesn't return a Ttl

// EXPERIMENTAL: AWS documents an idle-connection timeout (~10 minutes) on
// these signaling WebSockets — once the SDP/ICE handshake is done, no more
// signaling traffic normally flows, so the socket goes idle and AWS closes
// it. This periodically resends the last known local ICE candidate (using
// the signaling client's own public sendIceCandidate() method — there's no
// documented "ping"/keepalive API) purely to keep the WSS socket non-idle.
// AWS doesn't document whether this actually resets their idle timer, so
// treat this as a real attempt, not a guarantee — the reconnect logic above
// is still what actually recovers the stream if the connection drops
// anyway, keepalive or not.
const SIGNALING_KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes — well under the ~10 minute idle limit

// Separate problem from a dropped ICE path: sometimes the connection stays
// "connected" (keepalives still succeed) but actual video frames stop
// arriving — the picture just freezes while status still says "live".
// This watchdog polls decoder stats directly so a frozen feed gets caught
// even when iceConnectionState/connectionState never change.
const STALL_CHECK_INTERVAL_MS = 1000;   // how often to check the frame counter
const STALL_MAX_STALE_CHECKS = 3;       // ~3s of zero new frames before we force a reconnect

// On mobile data, a single attempt can get stuck for a long time — not
// because our code is slow, but because one of the AWS SDK calls (or the
// signaling WebSocket's own open()) can hang waiting on a dead connection
// with no timeout of its own. If the SETUP phase (AWS calls through
// opening the signaling socket and sending our SDP offer) hasn't produced
// an answer within this window, something is genuinely stuck — give up
// and let the backoff loop retry.
const SETUP_TIMEOUT_MS = 8000;

// Once we've received the SDP answer, we know signaling is fine and real
// ICE/TURN negotiation is underway — that alone can legitimately take
// longer on mobile (relay allocation, connectivity checks across candidate
// pairs), so it gets its own, longer budget rather than sharing the setup
// timeout above.
const ICE_TIMEOUT_MS = 15000;

// This is our main component. It shows the video and buttons.
// usePiViewer() holds ALL of the WebRTC/KVS connection logic, state and
// refs, with no JSX of its own. This is what makes it possible for
// more than one UI to drive the exact same connection code — the
// standalone <PiViewer /> component below uses it, and so does
// CameraView.jsx's own styled panel, without duplicating any of the
// signaling/reconnect/stall-watchdog logic between them.
export function usePiViewer() {
    const videoRef = useRef(null);

    // pcRef stores our WebRTC connection object (so it survives re-renders)
    const pcRef = useRef(null);

    // signalingClientRef stores our connection to AWS (so it survives re-renders)
    const signalingClientRef = useRef(null);

    // status holds the current state (idle/connecting/live/error/closed)
    // setStatus is the function we call to change it
    const [status, setStatus] = useState(STATUS.IDLE);

    // errorMessage holds any error text to show the user
    const [errorMessage, setErrorMessage] = useState(null);

    // logEntries holds a running history of what happened and when —
    // connects, drops, reconnect attempts, measured downtime — so you can
    // review or export it after a field test instead of only seeing it
    // scroll past in the browser console.
    const [logEntries, setLogEntries] = useState([]);

    // addLogEntry() timestamps a message and appends it to the on-screen
    // log, keeping only the most recent 300 entries so it can't grow
    // forever during a long unattended run.
    const addLogEntry = useCallback((message) => {
        const entry = { time: new Date().toISOString(), message };
        setLogEntries((prev) => {
            const next = [...prev, entry];
            return next.length > 300 ? next.slice(next.length - 300) : next;
        });
    }, []);

    // reconnectTimeoutRef stores the pending setTimeout id for a scheduled
    // reconnect attempt, so we can cancel it if the user stops manually
    // or the component unmounts
    const reconnectTimeoutRef = useRef(null);

    // reconnectAttemptsRef counts consecutive failed reconnect attempts,
    // used to back off (wait longer between tries) and to eventually stop
    const reconnectAttemptsRef = useRef(0);

    // manualStopRef tracks whether the user clicked "Stop" on purpose —
    // if so, we should NOT auto-reconnect when cleanup() runs
    const manualStopRef = useRef(false);

    // startViewingRef always points at the latest startViewing function.
    // scheduleReconnect() below needs to call startViewing, but startViewing
    // is defined further down — this ref sidesteps that ordering problem.
    const startViewingRef = useRef(null);

    // statsIntervalRef holds the stall-watchdog's setInterval id
    const statsIntervalRef = useRef(null);

    // keepaliveIntervalRef holds the signaling-keepalive setInterval id.
    // lastLocalCandidateRef remembers the most recent local ICE candidate
    // we sent, so the keepalive has something real to resend.
    const keepaliveIntervalRef = useRef(null);
    const lastLocalCandidateRef = useRef(null);

    // lastFramesDecodedRef / staleChecksRef track the decoder's frame count
    // between watchdog ticks, so we can tell "no new frames" apart from
    // "just checked, all normal"
    const lastFramesDecodedRef = useRef(null);
    const staleChecksRef = useRef(0);

    // outageStartedAtRef records when the CURRENT outage was first detected
    // (by whichever detector — ICE state, signaling close/error, or the
    // stall watchdog — notices it first). Used purely to measure and log
    // the actual end-to-end "camera was down for Xms" time.
    const outageStartedAtRef = useRef(null);

    // lastFailureReasonRef remembers the most recent disconnect/retry-failure
    // reason, so the quiet "still retrying" heartbeat (see QUIET_RETRY_LOG_EVERY)
    // can still show what's actually going wrong during a long outage.
    const lastFailureReasonRef = useRef(null);

    // attemptIdRef gives every call to startViewing() a unique id. Async
    // code inside that attempt (AWS calls, the signaling 'open'/'sdpAnswer'
    // handlers, onicecandidate) checks its own captured id against this
    // ref before touching shared state or sending anything — if a newer
    // attempt has since started, the stale one becomes a safe no-op
    // instead of racing with it or sending on a closed socket.
    const attemptIdRef = useRef(0);

    // attemptTimeoutRef holds the timer that gives up on a single stuck
    // attempt (see SETUP_TIMEOUT_MS / ICE_TIMEOUT_MS above) and hands off to scheduleReconnect.
    const attemptTimeoutRef = useRef(null);

    // channelCacheRef holds { channelARN, wssEndpoint, httpsEndpoint } once
    // we've successfully resolved them once. Reused on every later
    // reconnect instead of asking AWS again — these don't change for a
    // given channel name. Seeded from sessionStorage so a page refresh
    // doesn't lose it and force a fresh AWS resolve unnecessarily.
    const channelCacheRef = useRef(loadFromSessionStorage(SESSION_STORAGE_CHANNEL_KEY));

    // iceServersCacheRef holds { iceServers, expiresAt } — the last fetched
    // TURN/STUN server list and when its credentials actually expire.
    // Reused as long as it's not close to expiring; refetched otherwise.
    // Also seeded from sessionStorage, but only trusted if it hasn't
    // actually expired since the page was last open — a stale TURN
    // credential from an hour-old tab is worse than no cache at all.
    const iceServersCacheRef = useRef((() => {
        const cached = loadFromSessionStorage(SESSION_STORAGE_ICE_KEY);
        if (cached && typeof cached.expiresAt === 'number' && Date.now() < cached.expiresAt - ICE_SERVERS_REFRESH_MARGIN_MS) {
            return cached;
        }
        if (cached) clearSessionStorage(SESSION_STORAGE_ICE_KEY); // expired — don't leave stale data sitting around
        return null;
    })());

    // cleanup() closes everything down safely — closes the video
    // connection, closes the AWS connection, and clears the video screen
    const cleanup = useCallback((clearVideo = true) => {
        console.log(`[PiViewer] Cleanup Step 1: cleanup() called — closing connections (clearVideo=${clearVideo})`);

        // Cancel any pending auto-reconnect attempt so we don't end up with
        // two connections racing each other
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        // Invalidate any in-flight attempt (AWS calls, signaling handlers)
        // so it becomes a no-op if it eventually resolves after we've
        // already moved on — this is what makes it safe to give up on a
        // stuck attempt without waiting for it to actually finish.
        attemptIdRef.current += 1;

        if (attemptTimeoutRef.current) {
            clearTimeout(attemptTimeoutRef.current);
            attemptTimeoutRef.current = null;
        }

        // Stop the stall watchdog too
        if (statsIntervalRef.current) {
            clearInterval(statsIntervalRef.current);
            statsIntervalRef.current = null;
        }
        lastFramesDecodedRef.current = null;
        staleChecksRef.current = 0;

        // And the signaling keepalive
        if (keepaliveIntervalRef.current) {
            clearInterval(keepaliveIntervalRef.current);
            keepaliveIntervalRef.current = null;
        }

        if (pcRef.current) {
            // Detach our state-change listeners first so closing the old
            // connection can't itself trigger another reconnect
            pcRef.current.oniceconnectionstatechange = null;
            pcRef.current.onconnectionstatechange = null;
            pcRef.current.close(); // close the WebRTC connection
        }
        pcRef.current = null; // forget it, so we don't close it twice

        signalingClientRef.current?.close(); // close the AWS connection if it exists
        signalingClientRef.current = null; // forget it too

        if (clearVideo && videoRef.current) {
            videoRef.current.srcObject = null; // clear the video screen
        }
        // When clearVideo is false (an automatic reconnect), we deliberately
        // leave the old srcObject in place. The underlying tracks are already
        // ending because pc.close() ran above, so playback just freezes on
        // the last real frame instead of the screen snapping to blank/black
        // — much less jarring during a reconnect that may only take a couple
        // of seconds. It gets replaced with the new stream in ontrack(), or
        // explicitly cleared by stopViewing()/unmount if we never reconnect.

        console.log('[PiViewer] Cleanup Step 2: cleanup() complete');
    }, []);

    // scheduleReconnect() is called when we detect the live connection has
    // died underneath us (e.g. mobile carrier NAT silently dropped the UDP
    // path around the ~30s mark). Rather than sitting frozen forever, we
    // tear down and rebuild the whole session after a short, growing delay.
    const scheduleReconnect = useCallback((reason) => {
        if (manualStopRef.current) {
            // User pressed Stop on purpose — don't fight them by reconnecting
            return;
        }

        if (reconnectAttemptsRef.current === 0) {
            // This is the first sign of trouble for this outage — start the
            // stopwatch so we can log the real, measured downtime once
            // video comes back.
            outageStartedAtRef.current = performance.now();
        }
        lastFailureReasonRef.current = reason;

        reconnectAttemptsRef.current += 1;
        const attempt = reconnectAttemptsRef.current;

        if (attempt <= RECONNECT_MAX_ATTEMPTS) {
            // Log WHY every one of the first several attempts failed, not just
            // the very first — otherwise a long outage's log is a wall of
            // "Retrying..." lines with no way to tell if it's the same problem
            // repeating or something new each time.
            addLogEntry(`Connection lost (attempt ${attempt}): ${reason}`);
        }

        if (attempt === RECONNECT_MAX_ATTEMPTS + 1) {
            // We've tried the normal number of times without luck. For an
            // unattended robot camera, nobody may be watching to click
            // "Watch Stream" again — so we do NOT give up. We just stop
            // logging every single attempt from here on so a long outage
            // (e.g. waiting for the Pi's own reconnect loop, which can take
            // well over a minute) doesn't flood the log.
            addLogEntry(`Still no response after ${RECONNECT_MAX_ATTEMPTS} attempts (last reason: ${reason}) — camera or robot may be offline. Will keep retrying quietly every ${RECONNECT_MAX_DELAY_MS / 1000}s.`);
        } else if (attempt > RECONNECT_MAX_ATTEMPTS && attempt % QUIET_RETRY_LOG_EVERY === 0) {
            // Periodic "still trying" heartbeat during a long outage, so the
            // log shows we're alive and retrying rather than going silent.
            const downtimeSoFar = outageStartedAtRef.current !== null
                ? Math.round((performance.now() - outageStartedAtRef.current) / 1000)
                : null;
            addLogEntry(`Still retrying (last reason: ${lastFailureReasonRef.current}) — camera has been unreachable for ~${downtimeSoFar}s`);
        }

        // Exponential backoff, capped at RECONNECT_MAX_DELAY_MS: 1s, 2s, 4s, 8s, 10s, 10s...
        // and it stays capped there indefinitely — we never stop retrying on our own.
        const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1), RECONNECT_MAX_DELAY_MS);
        console.log(`[PiViewer] Reconnect: connection lost (${reason}) — attempt ${attempt} in ${delay}ms`);

        setStatus(STATUS.RECONNECTING);
        cleanup(false); // close the dead connection, but leave the last frame on screen instead of blanking it

        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            startViewingRef.current?.(/* isReconnect */ true);
        }, delay);
    }, [cleanup, addLogEntry]);

    // startStallWatchdog() begins polling the peer connection's decoder
    // stats. A frozen feed where the network path looks fine (ICE stays
    // "connected") but no new frames are arriving won't trip the ICE-state
    // listeners at all — this is what actually catches that case.
    const startStallWatchdog = useCallback(() => {
        if (statsIntervalRef.current) return; // already running, don't stack intervals

        lastFramesDecodedRef.current = null;
        staleChecksRef.current = 0;

        statsIntervalRef.current = setInterval(async () => {
            const pc = pcRef.current;
            if (!pc) return;
            try {
                const stats = await pc.getStats();
                let framesDecoded = null;
                stats.forEach((report) => {
                    if (report.type === 'inbound-rtp' && (report.kind === 'video' || report.mediaType === 'video')) {
                        framesDecoded = report.framesDecoded;
                    }
                });
                if (framesDecoded === null || framesDecoded === undefined) return; // stats not ready yet

                if (framesDecoded === lastFramesDecodedRef.current) {
                    staleChecksRef.current += 1;
                    console.log(`[PiViewer] stall watchdog: no new frames decoded (${staleChecksRef.current}/${STALL_MAX_STALE_CHECKS})`);
                    if (staleChecksRef.current >= STALL_MAX_STALE_CHECKS) {
                        console.log('[PiViewer] stall watchdog: video is frozen (ICE still reports connected) — forcing reconnect');
                        scheduleReconnect('video stalled: no new frames decoded');
                    }
                } else {
                    lastFramesDecodedRef.current = framesDecoded;
                    staleChecksRef.current = 0;
                }
            } catch (e) {
                console.log('[PiViewer] stall watchdog: getStats() failed', e);
            }
        }, STALL_CHECK_INTERVAL_MS);
    }, [scheduleReconnect]);

    // startSignalingKeepalive() periodically resends the last local ICE
    // candidate over the signaling WebSocket — see SIGNALING_KEEPALIVE_INTERVAL_MS
    // above for why. This is intentionally NOT tied to attemptId the way most
    // other things in this file are: it's called with an explicit reference to
    // the signaling client and candidate it should use, so it can't accidentally
    // send on a client that's already been superseded.
    const startSignalingKeepalive = useCallback((signalingClient, myAttemptId) => {
        if (keepaliveIntervalRef.current) clearInterval(keepaliveIntervalRef.current);

        keepaliveIntervalRef.current = setInterval(() => {
            if (attemptIdRef.current !== myAttemptId) return; // this attempt is stale — a newer one has taken over
            const candidate = lastLocalCandidateRef.current;
            if (!candidate) return; // nothing to resend yet

            try {
                signalingClient.sendIceCandidate(candidate);
                console.log('[PiViewer] Sent signaling keepalive (resent last ICE candidate)');
            } catch (e) {
                // Not fatal — if the socket's actually dead, the ICE/stall
                // watchdogs will already be moving toward a reconnect anyway.
                console.log('[PiViewer] Signaling keepalive failed to send (socket likely closed)', e);
            }
        }, SIGNALING_KEEPALIVE_INTERVAL_MS);
    }, []);

    // startViewing() is the main function — it does everything needed
    // to connect to the  Pi's camera and start watching
    const startViewing = useCallback(async (isReconnect = false) => {
        console.log(`[PiViewer] Step 1: startViewing() called (isReconnect=${isReconnect})`);

        if (!isReconnect) {
            // This is a fresh, user-initiated start (button click), not an
            // automatic retry — reset the manual-stop flag and the backoff
            // counter so a new session gets the full retry budget
            manualStopRef.current = false;
            reconnectAttemptsRef.current = 0;
            addLogEntry('Watch Stream clicked — connecting');
        } else {
            addLogEntry(`Retrying connection (attempt ${reconnectAttemptsRef.current})`);
        }

        setStatus(isReconnect ? STATUS.RECONNECTING : STATUS.CONNECTING);
        setErrorMessage(null); // clear any old error message

        // Give this specific attempt an id, and start its own timeout.
        // Any async code below checks myAttemptId against attemptIdRef
        // before touching shared refs or sending anything — if a newer
        // attempt has since started (because this one got abandoned via
        // the timeout, or the user stopped/retried), this becomes a
        // harmless no-op instead of corrupting state or sending on a
        // socket that's already closed.
        attemptIdRef.current += 1;
        const myAttemptId = attemptIdRef.current;
        const myClientId = makeClientId(); // fresh identity for this attempt — see makeClientId() above for why

        if (attemptTimeoutRef.current) clearTimeout(attemptTimeoutRef.current);
        attemptTimeoutRef.current = setTimeout(() => {
            if (attemptIdRef.current !== myAttemptId) return; // already superseded
            console.log(`[PiViewer] Attempt ${myAttemptId} timed out after ${SETUP_TIMEOUT_MS}ms during signaling setup (no SDP answer yet) — abandoning it`);
            scheduleReconnect(`signaling setup timed out after ${SETUP_TIMEOUT_MS}ms`);
        }, SETUP_TIMEOUT_MS);

        try {
            let channelARN, wssEndpoint, httpsEndpoint;

            if (channelCacheRef.current) {
                // Already resolved this channel once before — reuse it. The
                // channel ARN and its endpoints don't change for a given
                // channel name, so there's nothing to gain by asking AWS again.
                ({ channelARN, wssEndpoint, httpsEndpoint } = channelCacheRef.current);
                console.log('[PiViewer] Reusing cached channel ARN/endpoints — skipping DescribeSignalingChannel + GetSignalingChannelEndpoint', { channelARN });
            } else {
                // STEP 1: Ask AWS "where is this channel, and how do I connect to it?"
                console.log('[PiViewer] Step 2: Describing signaling channel', { CHANNEL_NAME, REGION });

                // Create a connection to AWS using our region and login details
                const kvClient = new KinesisVideoClient({ region: REGION, credentials: CREDENTIALS });

                // Ask AWS: "give me details about this channel name"
                const { ChannelInfo } = await kvClient.send(
                    new DescribeSignalingChannelCommand({ ChannelName: CHANNEL_NAME })
                );
                // Save the channel's unique ID (like its official address)
                channelARN = ChannelInfo.ChannelARN;
                console.log('[PiViewer] Step 3: got channel ARN', channelARN);

                // If a newer attempt has since started (e.g. this one got
                // abandoned by the attempt timeout while this call was still
                // hanging), stop here rather than acting on stale results.
                if (attemptIdRef.current !== myAttemptId) {
                    console.log(`[PiViewer] Attempt ${myAttemptId} superseded — stopping after DescribeSignalingChannel`);
                    return;
                }

                console.log('[PiViewer] Step 4: Getting signaling channel endpoint (viewer role)');
                // Ask AWS: "give me the web addresses to connect to this channel,
                // as a VIEWER (someone watching, not the camera itself)"
                const { ResourceEndpointList } = await kvClient.send(
                    new GetSignalingChannelEndpointCommand({
                        ChannelARN: channelARN,
                        SingleMasterChannelEndpointConfiguration: {
                            Protocols: ['WSS', 'HTTPS'], // we need both a chat address (WSS) and a normal web address (HTTPS)
                            Role: Role.VIEWER, // tell AWS "I am a viewer, not the camera"
                        },
                    })
                );
                // Turn the list of addresses into an easy-to-use object,
                // like { WSS: "...", HTTPS: "..." }
                const endpoints = Object.fromEntries(
                    ResourceEndpointList.map((e) => [e.Protocol, e.ResourceEndpoint])
                );
                wssEndpoint = endpoints.WSS;
                httpsEndpoint = endpoints.HTTPS;
                console.log('[PiViewer] Step 5: got endpoints', endpoints);

                // Cache it for every future reconnect attempt.
                channelCacheRef.current = { channelARN, wssEndpoint, httpsEndpoint };
                saveToSessionStorage(SESSION_STORAGE_CHANNEL_KEY, channelCacheRef.current);
            }

            if (attemptIdRef.current !== myAttemptId) {
                console.log(`[PiViewer] Attempt ${myAttemptId} superseded — stopping after channel resolution`);
                return;
            }

            // STEP 2: Ask AWS for STUN/TURN servers — these help our video
            // connection work even through tricky routers/firewalls
            let iceServers;
            const nowMs = Date.now();
            const cachedIce = iceServersCacheRef.current;

            if (cachedIce && nowMs < cachedIce.expiresAt - ICE_SERVERS_REFRESH_MARGIN_MS) {
                iceServers = cachedIce.iceServers;
                console.log('[PiViewer] Reusing cached ICE server list — skipping GetIceServerConfig', { msUntilExpiry: cachedIce.expiresAt - nowMs });
            } else {
                console.log('[PiViewer] Step 6: Requesting ICE server config (STUN/TURN)');

                // Create a connection to AWS's "signaling" service
                const signalingChannelClient = new KinesisVideoSignalingClient({
                    region: REGION,
                    credentials: CREDENTIALS,
                    endpoint: httpsEndpoint, // use the web address we got above
                });
                // Ask AWS: "give me a list of helper servers (STUN/TURN) to use"
                const { IceServerList } = await signalingChannelClient.send(
                    new GetIceServerConfigCommand({ ChannelARN: channelARN })
                );
                // Build our final list of helper servers:
                // - one basic STUN server (finds our public internet address)
                // - plus all the TURN servers AWS gave us (relay video if needed)
                let minTtlSeconds = ICE_SERVERS_DEFAULT_TTL_S;
                iceServers = [
                    { urls: `stun:stun.kinesisvideo.${REGION}.amazonaws.com:443` },
                    ...IceServerList.map((s) => {
                        if (typeof s.Ttl === 'number') minTtlSeconds = Math.min(minTtlSeconds, s.Ttl);
                        return {
                            urls: s.Uris,
                            username: s.Username,
                            credential: s.Password,
                        };
                    }),
                ];
                console.log('[PiViewer] Step 7: built ICE server list', { count: iceServers.length, ttlSeconds: minTtlSeconds });

                iceServersCacheRef.current = { iceServers, expiresAt: Date.now() + minTtlSeconds * 1000 };
                saveToSessionStorage(SESSION_STORAGE_ICE_KEY, iceServersCacheRef.current);
            }

            if (attemptIdRef.current !== myAttemptId) {
                console.log(`[PiViewer] Attempt ${myAttemptId} superseded — stopping after GetIceServerConfig`);
                return;
            }

            // STEP 3: Open a live "chat line" (WebSocket) to AWS, as a VIEWER
            console.log('[PiViewer] Step 8: Creating signaling client and WebRTC peer connection');

            // Create the AWS signaling client — this is what talks to AWS
            // in real time to swap connection details with the Pi
            const signalingClient = new SignalingClient({
                channelARN,                 // which channel to join
                channelEndpoint: wssEndpoint, // the chat address to connect to
                clientId: myClientId,        // our unique name (fresh for this attempt)
                role: Role.VIEWER,          // we are the viewer, not the camera
                region: REGION,
                credentials: CREDENTIALS,
            });
            // Save it so we can close it later
            signalingClientRef.current = signalingClient;

            // Create our actual WebRTC connection (this carries the video)
            const pc = new RTCPeerConnection({ iceServers });
            pcRef.current = pc; // save it so we can close it later

            // Tell WebRTC: "I only want to RECEIVE video, I'm not sending any"
            pc.addTransceiver('video', { direction: 'recvonly' });
            console.log('[PiViewer] Step 9: peer connection created, recvonly video transceiver added');

            // Watch the ICE connection state. On mobile data, the carrier's
            // NAT can silently drop the UDP mapping (commonly ~30s in) —
            // WebRTC surfaces that as "disconnected" (path lost, may recover
            // briefly on its own) or "failed" (path is gone for good, e.g.
            // "Consent to send expired" in the logs). We don't wait around;
            // we rebuild the session via scheduleReconnect().
            pc.oniceconnectionstatechange = () => {
                const state = pc.iceConnectionState;
                console.log('[PiViewer] oniceconnectionstatechange:', state);
                if (state === 'failed') {
                    scheduleReconnect(`ice ${state}`);
                } else if (state === 'disconnected') {
                    // "disconnected" can sometimes self-heal within a couple
                    // seconds, but on mobile CGNAT it usually doesn't — give
                    // it a brief moment, then reconnect if still not back.
                    setTimeout(() => {
                        if (pcRef.current === pc && pc.iceConnectionState === 'disconnected') {
                            scheduleReconnect('ice disconnected (did not self-heal)');
                        }
                    }, 2000);
                }
            };

            // connectionState is the broader signal (covers ICE + DTLS) —
            // "failed" here is unambiguous, so reconnect immediately.
            pc.onconnectionstatechange = () => {
                console.log('[PiViewer] onconnectionstatechange:', pc.connectionState);
                if (pc.connectionState === 'failed') {
                    scheduleReconnect(`connection ${pc.connectionState}`);
                } else if (pc.connectionState === 'connected') {
                    // This — not ontrack — is the real signal that ICE actually
                    // finished connecting. ontrack fires as soon as the SDP answer
                    // is negotiated, which can happen well before ICE/DTLS actually
                    // completes, so a timer that only ontrack could cancel would be
                    // left running and fire later against a connection that's
                    // genuinely fine (that was the cause of the ~15s reconnect loop).
                    if (attemptTimeoutRef.current) {
                        clearTimeout(attemptTimeoutRef.current);
                        attemptTimeoutRef.current = null;
                    }
                }
            };

            // This runs automatically when video actually starts arriving
            pc.ontrack = (event) => {
                console.log('[PiViewer] Step 10: ontrack event — video stream received from Pi');
                if (videoRef.current) {
                    // Put the incoming video into our <video> tag on screen
                    videoRef.current.srcObject = event.streams[0];
                }
                setStatus(STATUS.LIVE); // tell the screen "we are live now!"

                // This attempt succeeded — it no longer needs a timeout watching it.
                if (attemptTimeoutRef.current) {
                    clearTimeout(attemptTimeoutRef.current);
                    attemptTimeoutRef.current = null;
                }

                if (outageStartedAtRef.current !== null) {
                    // We were mid-outage — log exactly how long the camera was actually down,
                    // from first detection to first frame back on screen.
                    const downtimeMs = Math.round(performance.now() - outageStartedAtRef.current);
                    console.log(`[PiViewer] Reconnect: camera back after ${downtimeMs}ms downtime (${reconnectAttemptsRef.current} attempt(s))`);
                    addLogEntry(`Camera back — was down for ${(downtimeMs / 1000).toFixed(1)}s (${reconnectAttemptsRef.current} attempt(s))`);
                    outageStartedAtRef.current = null;
                } else {
                    addLogEntry('Camera live');
                }
                reconnectAttemptsRef.current = 0; // fresh video means the reconnect worked — reset backoff
                startStallWatchdog(); // now that frames are flowing, start watching that they keep flowing
                startSignalingKeepalive(signalingClient, myAttemptId); // try to keep the signaling socket from going idle
            };

            // This runs automatically whenever our side finds a new possible
            // network path (candidate) — we must send it to the Pi
            pc.onicecandidate = ({ candidate }) => {
                if (!candidate) return;
                if (attemptIdRef.current !== myAttemptId) return; // this attempt is stale — a newer one has already taken over
                console.log('[PiViewer] Step 11: onicecandidate event — sending local ICE candidate', candidate);
                lastLocalCandidateRef.current = candidate;
                try {
                    // send this network path info to AWS, which forwards it to the Pi
                    signalingClient.sendIceCandidate(candidate);
                } catch (e) {
                    // The signaling socket can close between us finding a candidate and
                    // sending it (e.g. the network just dropped) — this is exactly the
                    // "connection to the signaling service is not open" crash. It's not
                    // fatal: the ICE/stall watchdogs will already be moving us toward a
                    // reconnect, so just log it instead of letting it go unhandled.
                    console.log('[PiViewer] Failed to send ICE candidate (signaling likely closed)', e);
                }
            };

            // This runs automatically once our chat line to AWS is open
            signalingClient.on('open', async () => {
                if (attemptIdRef.current !== myAttemptId) return; // stale attempt — ignore
                console.log('[PiViewer] Step 12: signaling client "open" event — creating and sending SDP offer');
                try {
                    // Create an "offer" — basically saying "here's what I can receive"
                    const offer = await pc.createOffer({ offerToReceiveVideo: true });
                    // Save this offer as our own description
                    await pc.setLocalDescription(offer);

                    if (attemptIdRef.current !== myAttemptId) return; // dropped out while we were awaiting

                    // Send the offer to AWS, which forwards it to the Pi
                    signalingClient.sendSdpOffer(pc.localDescription);
                    console.log('[PiViewer] Step 13: SDP offer sent');
                } catch (e) {
                    // Network can drop between "socket opened" and "offer sent" — that's
                    // this exact crash from the screenshot ("connection to the signaling
                    // service is not open"). Route it through the normal reconnect path
                    // instead of letting it go unhandled.
                    console.log('[PiViewer] Failed to create/send SDP offer', e);
                    addLogEntry(`Error sending offer: ${e.message || 'signaling closed mid-offer'}`);
                    scheduleReconnect('failed to send SDP offer');
                }
            });

            // This runs automatically when the Pi replies with an "answer"
            signalingClient.on('sdpAnswer', async (answer) => {
                if (attemptIdRef.current !== myAttemptId) return; // stale attempt — ignore
                console.log('[PiViewer] Step 14: sdpAnswer event — answer received from Pi', answer);
                try {
                    // Save the Pi's answer as the "other side's" description
                    await pc.setRemoteDescription(answer);
                    console.log('[PiViewer] Step 15: remote description set from SDP answer');

                    // Signaling setup is proven to be working — replace the short
                    // setup-phase timeout with a longer one that covers actual
                    // ICE/TURN connectivity checks, which can legitimately take a
                    // while on mobile (relay allocation, testing candidate pairs).
                    if (attemptTimeoutRef.current) clearTimeout(attemptTimeoutRef.current);
                    attemptTimeoutRef.current = setTimeout(() => {
                        if (attemptIdRef.current !== myAttemptId) return; // already superseded
                        console.log(`[PiViewer] Attempt ${myAttemptId} timed out after ${ICE_TIMEOUT_MS}ms waiting for ICE to connect (SDP answer was received, so signaling is fine — the network path itself isn't completing)`);
                        scheduleReconnect(`ICE negotiation timed out after ${ICE_TIMEOUT_MS}ms`);
                    }, ICE_TIMEOUT_MS);
                } catch (e) {
                    // A duplicate/late answer arriving after we're already
                    // successfully connected is harmless — the connection is
                    // fine, we just got redundant signaling. Don't tear down a
                    // working stream over it. "wrong state: stable" specifically
                    // means an answer was already applied once.
                    const alreadyConnected = pc.signalingState === 'stable' &&
                        (pc.connectionState === 'connected' || pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed');
                    if (alreadyConnected) {
                        console.log('[PiViewer] Ignoring redundant/duplicate SDP answer — already connected', e.message);
                    } else {
                        console.log('[PiViewer] Failed to set remote description from SDP answer', e);
                        addLogEntry(`Error applying answer: ${e.message || 'unknown'}`);
                        scheduleReconnect('failed to apply SDP answer');
                    }
                }
            });

            // This runs automatically when the Pi sends us a network path (candidate)
            signalingClient.on('iceCandidate', (candidate) => {
                if (attemptIdRef.current !== myAttemptId) return; // stale attempt — ignore
                console.log('[PiViewer] Step 16: iceCandidate event — candidate received from Pi', candidate);
                try {
                    // Add that network path to our connection
                    pc.addIceCandidate(candidate);
                } catch (e) {
                    console.log('[PiViewer] Failed to add remote ICE candidate', e);
                }
            });

            // This runs automatically if our chat line to AWS closes.
            // AWS can close this for reasons unrelated to the video path
            // (idle timeout on the signaling socket, etc.), so treat it the
            // same as a dropped connection and try to recover automatically
            // rather than just freezing on CLOSED.
            signalingClient.on('close', () => {
                if (attemptIdRef.current !== myAttemptId) return; // stale attempt (likely closed by our own cleanup()) — ignore
                console.log('[PiViewer] Step 17: signaling client "close" event');
                if (!manualStopRef.current) {
                    scheduleReconnect('signaling client closed');
                } else {
                    setStatus(STATUS.CLOSED); // tell the screen "connection closed" (user-initiated)
                }
            });

            // This runs automatically if something goes wrong with signaling
            signalingClient.on('error', (err) => {
                if (attemptIdRef.current !== myAttemptId) return; // stale attempt — ignore
                console.log('[PiViewer] Step 18: signaling client "error" event', err);
                setErrorMessage(err.message || 'Signaling error'); // show the error
                scheduleReconnect(`signaling error: ${err.message || 'unknown'}`);
            });

            // Actually start the connection process now
            console.log('[PiViewer] Step 19: opening signaling client connection...');
            signalingClient.open();
        } catch (err) {
            // If anything above throws an error, we land here
            console.log('[PiViewer] Step 20: error caught in startViewing()', err);
            if (attemptTimeoutRef.current) {
                clearTimeout(attemptTimeoutRef.current);
                attemptTimeoutRef.current = null;
            }
            // Safety net: an actual thrown error (as opposed to a timeout)
            // could mean the cached channel/endpoint info has genuinely gone
            // stale (e.g. the channel was recreated). Drop the cache so the
            // next attempt re-resolves from scratch instead of repeating
            // the same failure indefinitely.
            channelCacheRef.current = null;
            clearSessionStorage(SESSION_STORAGE_CHANNEL_KEY);
            setErrorMessage(err.message || 'Failed to connect to robot camera.'); // show error text
            // Same reasoning as the timeout paths: don't hard-stop and wait for
            // someone to click "Watch Stream" again — keep retrying in the
            // background. scheduleReconnect() itself checks manualStopRef, so a
            // deliberate Stop still won't be fought.
            scheduleReconnect(`error: ${err.message || 'unknown'}`);
        }
    }, [cleanup, scheduleReconnect, startStallWatchdog, startSignalingKeepalive, addLogEntry]); // this function depends on cleanup, scheduleReconnect, startStallWatchdog, startSignalingKeepalive and addLogEntry, so re-make it if any change

    // stopViewing() is called when the user clicks "Stop"
    const stopViewing = useCallback(() => {
        console.log('[PiViewer] stopViewing() called — triggering cleanup');
        addLogEntry('Stopped by user');
        manualStopRef.current = true; // this was on purpose — don't auto-reconnect
        cleanup(); // close everything (also cancels any pending reconnect timer)
        setStatus(STATUS.IDLE); // go back to the starting state
    }, [cleanup, addLogEntry]);

    // Keep startViewingRef pointing at the latest startViewing function.
    // scheduleReconnect() calls through this ref instead of depending on
    // startViewing directly, avoiding a circular useCallback dependency.
    useEffect(() => {
        startViewingRef.current = startViewing;
    }, [startViewing]);

    // When this component disappears from the screen (unmounts), mark it as
    // a manual stop (so no reconnect fires after we're gone) and clean up
    useEffect(() => {
        return () => {
            manualStopRef.current = true;
            cleanup();
        };
    }, [cleanup]);

    // downloadLog() saves the current event log to a .txt file the browser
    // downloads, e.g. for attaching to a bug report or reviewing after an
    // unattended field test.
    const downloadLog = useCallback(() => {
        const text = logEntries.map((e) => `${e.time}  ${e.message}`).join('\n');
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pi-viewer-log-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [logEntries]);

    // A simple true/false flag: are we currently trying to watch or watching?
    const isWatching = status === STATUS.CONNECTING || status === STATUS.LIVE || status === STATUS.RECONNECTING;

    // Everything a consumer needs to drive its own camera UI: bind
    // videoRef to a <video> tag, read status/errorMessage/logEntries to
    // render, and call startViewing()/stopViewing() from your own
    // buttons or effects (e.g. auto-start once MQTT reports online).
    return {
        videoRef,
        pcRef,
        status,
        errorMessage,
        logEntries,
        isWatching,
        startViewing,
        stopViewing,
        downloadLog,
    };
}

// Standalone full component with its own title, video, status line,
// Watch Stream/Stop button and Event Log — unchanged from before, just
// now a thin wrapper around usePiViewer() so it shares the exact same
// connection logic as any other UI built on the hook (e.g.
// CameraView.jsx), rather than a second independent copy of it.
export function PiViewer() {
    const {
        videoRef,
        status,
        errorMessage,
        logEntries,
        isWatching,
        startViewing,
        stopViewing,
        downloadLog,
    } = usePiViewer();

    // This is what actually gets drawn on the screen
    return (
        <div className="kvs-viewer">
            {/* Title showing which channel we're watching */}
            <h2>Robot Camera — {CHANNEL_NAME}</h2>

            {/* The video screen itself, wrapped so we can overlay a
          "reconnecting" indicator on top of it. While reconnecting, the
          <video> tag still shows the last real frame (cleanup() leaves
          srcObject alone on an auto-reconnect — see cleanup() above) so
          the picture doesn't flash to black on every drop. But a frozen
          frame with no visual cue looks exactly like a working live feed,
          which is misleading on its own — this overlay is what actually
          tells the viewer "this is stale, not live" while it's frozen. */}
            <div className="kvs-viewer__video-wrap" style={{ position: 'relative' }}>
                {/* autoPlay = play automatically, playsInline = don't go
              fullscreen on mobile, controls = show play/pause/volume
              buttons. Dimmed + desaturated while reconnecting so the
              frozen frame itself reads as "not live" at a glance, on
              top of the overlay below. */}
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    controls
                    className="kvs-viewer__video"
                    style={status === STATUS.RECONNECTING ? { filter: 'grayscale(60%) brightness(55%)' } : undefined}
                />

                {status === STATUS.RECONNECTING && (
                    <div
                        className="kvs-viewer__reconnect-overlay"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            background: 'rgba(0, 0, 0, 0.25)', // on top of the dimmed video itself
                            color: '#fff',
                            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                            pointerEvents: 'none', // let clicks reach the video's own controls underneath
                        }}
                    >
                        <span
                            className="kvs-viewer__reconnect-spinner"
                            style={{
                                width: '2rem',
                                height: '2rem',
                                border: '3px solid rgba(255,255,255,0.35)',
                                borderTopColor: '#fff',
                                borderRadius: '50%',
                                animation: 'kvs-viewer-spin 0.8s linear infinite',
                            }}
                        />
                        <span style={{ fontWeight: 600 }}>Reconnecting… showing last frame</span>
                        {/* Keyframes can't go on inline styles — this tiny
                    <style> tag is the simplest way to define them without
                    depending on an external stylesheet existing at all. */}
                        <style>{'@keyframes kvs-viewer-spin { to { transform: rotate(360deg); } }'}</style>
                    </div>
                )}
            </div>

            {/* Show the current status as text, e.g. "Status: connecting".
          While auto-reconnecting (e.g. after a mobile CGNAT drop), say so
          explicitly so it reads as "handling it" rather than "broken". */}
            <p className="kvs-viewer__status">
                Status: {status === STATUS.RECONNECTING ? 'reconnecting…' : status}
            </p>

            {/* Only show this line if there IS an error message */}
            {errorMessage && <p className="kvs-viewer__error">{errorMessage}</p>}

            <div className="kvs-viewer__controls">
                {/* If we are NOT watching, show "Watch Stream" button.
            If we ARE watching, show "Stop" button instead. */}
                {!isWatching ? (
                    <button onClick={() => startViewing()}>Watch Stream</button>
                ) : (
                    <button onClick={stopViewing}>Stop</button>
                )}
            </div>

            {/* Event log: a timestamped history of connects, drops, retries,
          and measured downtime, so you can see what actually happened
          instead of only whatever's currently on screen. */}
            <div className="kvs-viewer__log">
                <div className="kvs-viewer__log-header">
                    <h3>Event Log</h3>
                    <button onClick={downloadLog} disabled={logEntries.length === 0}>
                        Download Log
                    </button>
                </div>
                {logEntries.length === 0 ? (
                    <p className="kvs-viewer__log-empty">No events yet.</p>
                ) : (
                    <ul className="kvs-viewer__log-list">
                        {/* Most recent entry on top */}
                        {[...logEntries].reverse().map((entry, i) => (
                            <li key={i}>
                                <span className="kvs-viewer__log-time">
                                    {new Date(entry.time).toLocaleTimeString()}
                                </span>{' '}
                                {entry.message}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
export default PiViewer

// Named exports so other components (e.g. CameraView.jsx) can read the
// channel name for display, or compare status against STATUS's values
// without hardcoding the strings themselves.
export { STATUS, CHANNEL_NAME };